import {AfterViewInit, Component, effect, ElementRef, HostListener, Input, signal, ViewChild} from '@angular/core';
import {CardConfig, PlayerMode, TactaService} from '@/_services/tacta.service';
import {TactaCanvas, TactaCardService} from '@/_services/tacta-card.service';
import {GLOBALS} from '@/_services/globals.service';
import {Utils} from '@/classes/utils';

@Component({
  selector: 'app-tacta-board',
  imports: [],
  templateUrl: './tacta-board.component.html',
  styleUrl: './tacta-board.component.scss'
})
export class TactaBoardComponent implements AfterViewInit {
  @ViewChild('canvasHtml', {static: false})
  canvasRef!: ElementRef<HTMLCanvasElement>;
  refresh = signal<boolean>(false);
  currentCursor: string = 'default';
  boardImg: HTMLImageElement;

  constructor(private host: ElementRef<HTMLElement>,
              public ts: TactaService,
              public tcs: TactaCardService) {
    this.boardImg = new Image();
    this.boardImg.src = 'assets/images/boards/board01.png';

    this.boardImg.onload = () => {
      this.ts.boardPattern = this.cvs.ctx.createPattern(this.boardImg, 'repeat');
      if (this.cvs != null) {
        this.clearBoard();
        this.tcs.drawBoard(this.cvs);
      }
    };
    effect(() => {
      // GLOBALS.isDebug needs to be accessed to activate effect on changes
      GLOBALS.isDebug;
      this.refresh();
      this.ts.refresh();
      if (this.cvs != null && this.tcs.ctx != null) {
        this.tcs.updateContext(this.cvs, this.tcs.ctx);
        this.tcs.drawBoard(this.cvs);
      }
    });
  }

  @Input()
  set canvas(cvs: TactaCanvas) {
    this.ts.boardCanvas.set(cvs);
  }

  get cvs() {
    return this.ts.boardCanvas();
  }

  ngAfterViewInit(): void {
    setTimeout(() => {
      this.tcs.htmlCanvas = this.canvasRef.nativeElement;
      const cvs = this.ts.boardCanvas();
      this.tcs.initCanvas(this.host.nativeElement.parentElement.getBoundingClientRect());
      Utils.ZoomConfig(
        this.tcs.htmlCanvas,
        cvs.scale, 0.1, 5,
        (scale, x, y) => {
          const rect = this.canvasRef.nativeElement.getBoundingClientRect();
          // console.log(cvs.xOrg, x - rect.x, cvs.scale, scale);
          x -= rect.x;
          y -= rect.y;
          let xd = cvs.xOrg - x;
          xd = xd / cvs.scale * scale;
          cvs.xOrg = x + xd;
          let yd = cvs.yOrg - y;
          yd = yd / cvs.scale * scale;
          cvs.yOrg = y + yd;
          cvs.scale = scale;
          this.ts.adjustDimensions(cvs, {
            cardWidth: CardConfig.defWidth * scale,
            cardHeight: CardConfig.defHeight * scale,
            cardBorder: CardConfig.defBorder * scale
          });
          this.ts.clearBoard();
          this.tcs.drawBoard(cvs);
        }, (_x, _y) => {
          this.tcs.xCvs = cvs.xOrg;
          this.tcs.yCvs = cvs.yOrg;
        }, (dx, dy) => {
          const cardCount = 2;
          cvs.xOrg = Math.min(
            Math.max(cvs.cardWidth * -cardCount, this.tcs.xCvs + dx),
            this.tcs.htmlCanvas.width + cardCount * cvs.cardWidth);
          cvs.yOrg = Math.min(
            Math.max(cvs.cardHeight * -cardCount, this.tcs.yCvs + dy),
            this.tcs.htmlCanvas.height + cardCount * cvs.cardHeight);
//         this.cvs.xOrg = // Math.max(Math.min(-this.ts.boundingRect.x, this.xCvs + dx),
//           canvas.width - this.ts.boundingRect.right - this.cvs.cardWidth;
// //          this.ts.boundingRect.width - canvas.width - this.cvs.cardWidth;
//         //);
//         this.cvs.yOrg = canvas.height / 2;// - this.ts.boundingRect.bottom - this.cvs.cardHeight;
          this.ts.clearBoard();
          this.tcs.drawBoard(cvs);
        }
      );
    });
  }

  findArea(cvs: TactaCanvas, x: number, y: number, fullCard = false): TactaCanvas {
    let ret: TactaCanvas;

    if (fullCard) {
      if (cvs.cardIdx !== 0
        && this.tcs.ctx.isPointInPath(cvs.cardRect, x, y)
        && this.ts.highestTurn()?.cardIdx === cvs.cardIdx) {
        return cvs;
      }
    } else {
      for (const key of Object.keys(cvs.paths)) {
        if (this.tcs.ctx?.isPointInPath(cvs.paths[key], x, y)) {
          // only mark the area when it was not used for connection between cards
          if (!cvs.parentLink.startsWith(key) && !cvs.cards.find(c => c.parentLink.endsWith(key))) {
            // only mark the area when there is a possible connection to this card
            if (this.ts.connectors[this.ts.markedCanvas()?.markedAreas + key] != null) {
              cvs.markedAreas = key;
              return cvs;
            }
          }
        }
      }
    }
    for (const card of cvs.cards) {
      ret = this.findArea(card, x, y, fullCard);
      if (ret != null) {
        return ret;
      }
    }
    return ret;
  }

  clearMarkedAreas(cvs: TactaCanvas) {
    cvs.markedAreas = '';
    cvs.isMarked = false;
    for (const card of cvs.cards) {
      this.clearMarkedAreas(card);
    }
  }

  clearBoard() {
    const canvas = this.canvasRef.nativeElement;
    this.ts.clearBoard(canvas.width, canvas.height);
  }

  @HostListener('mousemove', ['$event'])
  onMouseMove(evt: MouseEvent) {
    const rect = this.canvasRef.nativeElement.getBoundingClientRect();
    this.clearMarkedAreas(this.cvs);
    this.ts.currentArea = this.findArea(this.cvs, evt.clientX - rect.x, evt.clientY - rect.y);
    if (this.ts.currentArea == null && this.ts.markedCanvas() == null) {
      this.ts.currentArea = this.findArea(this.cvs, evt.clientX - rect.x, evt.clientY - rect.y, true);
      switch (this.ts.playerMode()) {
        case PlayerMode.PlaceCard:
          if (this.ts.currentArea != null) {
            this.clearMarkedAreas(this.cvs);
            // this.currentArea.isMarked = true;
            this.currentCursor = 'url(\'/assets/images/cursor-rotate.png\') 12 12, auto';
            this.tcs.drawBoard(this.cvs);
            return;
          }
          break;
        case PlayerMode.SelectCard:
          if (this.ts.currentArea != null) {
            this.clearMarkedAreas(this.cvs);
            this.ts.currentArea.isMarked = true;
            this.currentCursor = 'url(\'/assets/images/cursor-remove.png\') 12 12, auto';
            this.tcs.drawBoard(this.cvs);
            return;
          }
          break;
      }
    }
    this.currentCursor = this.ts.currentArea != null ? 'pointer' : 'default';
    this.tcs.drawBoard(this.cvs);
  }

  @HostListener('mousedown', ['$event'])
  onMouseDown(_evt: MouseEvent) {
    // if (this.currentArea == null) {
    //   this.xDown = evt.clientX;
    //   this.yDown = evt.clientY;
    //   this.xCvs = this.cvs.xOrg ?? 0;
    //   this.yCvs = this.cvs.yOrg ?? 0;
    //   return;
    // }
    if (this.ts.currentArea != null) {
      const playerIdx = this.ts.currentArea.playerIdx;
      // this.ts.players.findIndex(p => p.color === this.ts.cardColor(this.currentArea.cardIdx));
      switch (this.ts.playerMode()) {
        case PlayerMode.PlaceCard:
          if (playerIdx >= 0) {
            const oldIdx = this.ts.currentArea.cardIdx;
            if (oldIdx >= 128) {
              this.ts.currentArea.cardIdx -= 128;
            } else {
              this.ts.currentArea.cardIdx += 128;
            }
          }
          this.ts.currentArea.parentLink = this.cvs.config.flipArea(this.ts.currentArea.parentLink[0]) + this.ts.currentArea.parentLink.substring(1);
          this.clearBoard();
          this.ts.refresh.set(!this.ts.refresh());
          return;
        case PlayerMode.SelectCard:
          if (this.ts.currentArea?.isMarked) {
            if (playerIdx >= 0) {
              this.ts.playerIdx = playerIdx;
              switch (this.ts.currentArea.playerHand) {
                case 'f':
                  this.ts.players[playerIdx].cards = [this.ts.currentArea.cardIdx, ...this.ts.players[playerIdx].cards];
                  break;
                case 'b':
                  this.ts.players[playerIdx].cards = [...this.ts.players[playerIdx].cards, this.ts.currentArea.cardIdx];
                  break;
              }
              this.ts.removeCard(this.cvs, this.ts.currentArea.cardIdx);
            }
            this.ts.currentArea = null;
            this.clearBoard();
            this.ts.calcScore(this.cvs);
            this.ts.refresh.set(!this.ts.refresh());
            return;
          }
      }
    }
    this.tcs.moveMarkedToBoard(this.cvs, this.ts.currentArea);
    this.ts.currentArea = null;
    this.clearBoard();
    this.tcs.drawBoard(this.cvs);
    this.refresh.set(!this.refresh());
  }

  @HostListener('mouseup', ['$event'])
  onMouseUp(_evt: MouseEvent) {
    this.tcs.xDown = null;
    this.tcs.yDown = null;
  }
}
